'use client';

import React from 'react';
import { UserX } from 'lucide-react';
import Avatar from '@/components/ui/Avatar';
import { useTeam } from '@/context/TeamContext';

type Branch = 'live_events' | 'studio' | 'outreach' | 'internal';

// Backend integration: fetch from /api/time-off?date=today&status=approved
const STAFF_OUT = [
  {
    id: 'staff-out-001',
    name: 'Erin Nesslage',
    title: 'Co-Director of Education',
    type: 'Full day',
    note: 'Personal',
    affectedItems: 2,
    branches: ['studio'] as Branch[],
  },
];

const PARTIAL_AVAILABILITY = [
  {
    id: 'staff-partial-001',
    name: 'Eric Pinder',
    title: 'Artistic Director',
    available: 'After 2:30 PM',
    note: 'Morning conflict',
    branches: ['live_events', 'studio'] as Branch[],
  },
];

export default function StaffOutWidget() {
  const { activeTeam } = useTeam();
  const isLeadership = activeTeam.id === 'leadership';

  const visibleOut = isLeadership
    ? STAFF_OUT
    : STAFF_OUT.filter((s) => s.branches.some((b) => activeTeam.branches.includes(b)));

  const visiblePartial = isLeadership
    ? PARTIAL_AVAILABILITY
    : PARTIAL_AVAILABILITY.filter((s) => s.branches.some((b) => activeTeam.branches.includes(b)));

  const hasIssues = visibleOut.length > 0;

  return (
    <div className={`rounded-xl border shadow-card overflow-hidden ${hasIssues ? 'bg-red-50 border-red-100' : 'bg-white border-stone-100'}`}>
      <div className="px-4 py-3 border-b border-red-100 flex items-center gap-2">
        <UserX size={14} className="text-red-400" />
        <h3 className="text-[13px] font-semibold text-slate-700">Staff Out Today</h3>
        {visibleOut.length > 0 && (
          <span className="ml-auto text-[11px] font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">
            {visibleOut.length}
          </span>
        )}
      </div>
      <div className="px-4 py-3 space-y-3">
        {visibleOut.length === 0 && visiblePartial.length === 0 ? (
          <p className="text-[12px] text-slate-400 text-center py-2">No staff out for this team today.</p>
        ) : (
          <>
            {visibleOut.map((s) => (
              <div key={s.id} className="flex items-start gap-2.5">
                <Avatar name={s.name} size="xs" />
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-slate-800">{s.name}</p>
                  <p className="text-[11px] text-slate-400">{s.title}</p>
                  <p className="text-[11px] text-red-600 mt-0.5">{s.type} · {s.affectedItems} items affected</p>
                </div>
              </div>
            ))}
            {visiblePartial.map((s) => (
              <div key={s.id} className="flex items-start gap-2.5">
                <Avatar name={s.name} size="xs" />
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-slate-800">{s.name}</p>
                  <p className="text-[11px] text-slate-400">{s.title}</p>
                  <p className="text-[11px] text-amber-600 mt-0.5">Partial · {s.available}</p>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}